import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { AuthContext } from './AuthContext';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, setCurrentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const processPaymentSuccess = async () => {
      if (!sessionId) {
        setError('No session ID found');
        setLoading(false);
        return;
      }

      try {
        // Try to read session but don't block verification if absent
        const { data: { session } } = await supabase.auth.getSession();

        console.log('[PaymentSuccess] Starting verification for sessionId:', sessionId);

        // Verify the checkout session (allow no Authorization header)
        const headers = { 'Content-Type': 'application/json' };
        if (session?.access_token) {
          Object.assign(headers, {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY,
          });
        }
        const response = await fetch(`/functions/v1/verify-checkout-session`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ sessionId }),
        });

        const rawText = await response.text();
        let data;
        try {
          data = rawText ? JSON.parse(rawText) : {};
        } catch (e) {
          data = { raw: rawText };
        }
        console.log('[PaymentSuccess] Verify response status:', response.status, 'body:', data);

        if (!response.ok) {
          throw new Error(data.message || `Failed to verify payment (status ${response.status})`);
        }

        setSessionData(data);

        // If user was created during checkout, log them in
        if (data.userCreated && data.userEmail) {
          // Check if user needs to be logged in
          if (!currentUser) {
            // Send magic link or redirect to login
            const { error: signInError } = await supabase.auth.signInWithOtp({
              email: data.userEmail,
              options: {
                shouldCreateUser: false,
                emailRedirectTo: `${window.location.origin}/dashboard`,
              },
            });

            if (signInError) {
              console.error('Error sending login link:', signInError);
            }
          }
        }

        // Immediately proceed to post-billing redirect
        navigate('/post-billing');
        return;

      } catch (err) {
        console.error('[PaymentSuccess] Verification error:', err);
        setError(err.message || 'Unknown verification error');
        setLoading(false);
      }
    };

    processPaymentSuccess();
  }, [sessionId, currentUser, setCurrentUser]);

  const handleContinue = () => {
    navigate('/post-billing');
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Processing your payment...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we set up your account.
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/pricing')}>
          Back to Pricing
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Box sx={{ mb: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main' }} />
          </Box>
          
          <Typography variant="h4" gutterBottom>
            Payment Successful!
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph>
            {sessionData?.isFreeProduct ? 
              "Welcome! Your free account has been set up successfully." :
              "Thank you for your purchase! Your subscription is now active."
            }
          </Typography>

          {sessionData?.packageName && (
            <Typography variant="h6" sx={{ mb: 3 }}>
              Plan: {sessionData.packageName}
            </Typography>
          )}

          {!currentUser && sessionData?.userEmail && (
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                We've sent a login link to <strong>{sessionData.userEmail}</strong>. 
                Check your email to access your account.
              </Typography>
            </Alert>
          )}

          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleContinue}
              fullWidth
            >
              {currentUser ? 'Go to Dashboard' : 'Continue to Login'}
            </Button>
          </Box>

          {sessionData?.isFreeProduct && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Start chatting with our AI assistant and explore all the features!
            </Typography>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentSuccess; 